import { Button, Input, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import TopCard from "../../../components/common/TopCard";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetDeviceAllocatedQuery,
  useRevokeUserFromDeviceMutation,
} from "../../../features/device/deviceApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  GetDeviceAllocatedParamsType,
  GetDeviceAllocatedType,
  GetEmployeeParamsType,
} from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  backLink,
  selectSearchOption,
  selectStringOption,
  underscoreToCapitalizeString,
} from "../../../utils/utils";

enum Status {
  active = "status-success",
  inactive = "status-danger",
}

type GetEmployeeTypeParams = {
  attendanceType?: "remote" | "location" | "wifi";
} & GetEmployeeParamsType;

const searchTypeSelect = ["name", "email", "employee_id", "mobile"]?.map(
  (item) => ({
    label: underscoreToCapitalizeString(item),
    value: item,
  })
);

export default function DevicesDetailsList() {
  const location = useLocation();
  const { viewId } = useParams();
  const [selectedData, setSelectedData] = useState<
    GetDeviceAllocatedType["data"]["employees"]
  >([]);
  const { open, handleClose, handleOpen } = useModal<
    string,
    "ALLOCATE" | "CONFIRM"
  >();
  const { query } = useQueryParams();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleFilter,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetDeviceAllocatedParamsType>({
    defaultFilter: {
      search_type: "name",
      status: (query.status as GetEmployeeTypeParams["status"]) || "active",
    },
  });
  const { isError, error, isLoading, isFetching, data } =
    useGetDeviceAllocatedQuery({
      ...filter,
      id: viewId,
      search_type: undefined,
      ...(keyword ? { search_type: filter.search_type } : {}),
      search: keyword,
    });

  const [
    revokeUserFromDevice,
    {
      isError: isRevokeUserError,
      error: revokeUserError,
      isLoading: isRevokeUserLoading,
      isSuccess: isRevokeSuccess,
    },
  ] = useRevokeUserFromDeviceMutation();
  useError(isError, error, "Get device allocated data fail");
  useError(isRevokeUserError, revokeUserError, "Revoke user from device fail");

  const tabList = [
    {
      label: "Allocate List",
      link: `/admin/devices`,
      redirect: `/admin/devices/view/${viewId}`,
    },
    {
      label: "Unassigned List",
      link: `/admin/devices/un-allocate`,
      redirect: `/admin/devices/un-allocate/view/${viewId}`,
    },
  ];

  const columns: ColumnsType<GetDeviceAllocatedType["data"]["employees"][0]> = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      render: (val) => val || "-",
    },
    {
      title: "Employee Name",
      dataIndex: "member",
      render: (val) => val?.profile?.name || "-",
    },
    {
      title: "Email",
      dataIndex: "member",
      render: (val) => val?.profile?.email || "-",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobile",
      render: (val) => val || "-",
    },
    {
      title: "Department",
      dataIndex: "role",
      render: (val) => val?.department?.name || "-",
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "status",
      render: (val: keyof typeof Status) => {
        return <span className={Status[val]}>{capitalize(val)}</span>;
      },
    },
    // {
    //   title: "Action",
    //   align: "center",
    //   dataIndex: "id",
    //   render: (val) => (
    //     <Button className="action-button" size="small" icon={<DeleteIcon />} />
    //   ),
    // },
  ];

  useEffect(() => {
    if (isRevokeSuccess) {
      toast("success", "User revoked successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevokeSuccess]);

  return (
    <>
      <SidebarLayout title="Allocate List" tabList={tabList}>
        {/* {open.type === "ALLOCATE" && (
        <AssignDeviceModal
          open={open.open}
          data={open?.data}
          handleClose={handleClose}
        />
      )} */}
        {open.type === "CONFIRM" && (
          <ConfirmModal
            handleOk={() =>
              revokeUserFromDevice({
                data: selectedData?.map((item) => ({ userId: item?.id })),
                id: Number(viewId),
              })
            }
            confirmTitle={`Revoke ${
              selectedData?.length === 1 ? "user" : "users"
            }`}
            questionText={`Are you sure you want to revoke the ${
              selectedData?.length === 1 ? "user" : "users"
            }?`}
            open={open.open}
            handleClose={handleClose}
            okLoading={isRevokeUserLoading}
            okText="Confirm"
          />
        )}
        <TopCard
          size={16}
          align="center"
          leftChildren={
            <Link to={backLink(location.pathname, 2)}>
              <Button className="normal-dark-button" icon={<BackButtonIcon />}>
                Back
              </Button>
            </Link>
          }
        >
          <Button
            type="primary"
            onClick={() => handleOpen({ type: "CONFIRM" })}
            disabled={!selectedData?.length}
          >
            Revoke
          </Button>
        </TopCard>

        <CustomTable
          loading={isLoading || isFetching}
          columns={columns}
          dataSource={addKeyInArray(data?.data?.employees)}
          limit={limit}
          page={page}
          total={data?.data?.totalLength}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          rowSelection={{
            type: "checkbox",
            // selectedRowKeys: selectedData,
            onChange: (_, record) => {
              setSelectedData(
                record as GetDeviceAllocatedType["data"]["employees"]
              );
            },
            columnWidth: 40,
          }}
          filterSection={
            <>
              <Select
                showSearch
                placeholder="Search Type"
                value={filter.search_type}
                options={searchTypeSelect}
                onChange={(val) => handleFilter("search_type", val)}
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                popupMatchSelectWidth={false}
              />
              <Input
                key={filter.search_type}
                suffix={<SearchIcon />}
                placeholder={`Search by ${underscoreToCapitalizeString(
                  filter.search_type || "",
                  true
                )}`}
                style={{ minWidth: 170, maxWidth: 180 }}
                onChange={(val) => handleSearch(val)}
                allowClear
              />

              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(
                  Object.keys(Status),
                  "string",
                  true
                )}
                className="min-w-[90px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.status}
                onChange={(val) => handleFilter("status", val)}
              />
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}
